import MenuText from "../../../../i18n/MenuText";
import {
    ButtonBase,
    Checkbox,
    DialogContent,
    FormControlLabel,
    FormGroup,
    TextField,
    IconButton,
} from "@mui/material";
import _, { toInteger } from "lodash";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import NumPad from "react-numpad";
import { enqueueSnackbar } from "notistack";
import { arrayMove } from "react-sortable-hoc";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../../../AppContext";
import backIcon from "../../../../images/back-icon.svg";
import { ItemDialog, ItemSmallButton, useStyles } from "./style";
import { PosContext } from "../../../../shared/PosContext";
import { isRoleAdmin } from "../../../../frappeConfig";
import BundleConfigurator from "./BundleConfigurator";
import { useSaveUserSettings } from "../../../../shared/hooks/useFrappeDoctype";
import {
    calculateAddonTreeTotal,
    composeUnitAddonSelection,
    getAddonCode,
    getAllowedAddonCodes,
    initializeBundleSelections,
    materializeAddonTree,
    normalizeBundleDefinition,
    pruneSkippedNestedSelections,
    validateItemSelection,
} from "./addonTree";

const getItemBaseRate = (source) => {
    const values = [source?.standard_rate, source?.price_list_rate, source?.rate]
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value));
    return values.find((value) => value > 0) ?? values[0] ?? 0;
};

function AddItemWidget({
    item,
    onClose,
    open,
    onAddItem,
    onUpdateItem,
    pushItemWorker,
    editedOrderItem,
    kioskMode = false,
}) {
    const { t } = useTranslation();
    const classes = useStyles();
    const [selectedAddons, setSelectedAddons] = useState([]);
    const [selectedOptions, setSelectedOptions] = useState({});
    const [bundleStep, setBundleStep] = useState(0);
    const [bundleErrors, setBundleErrors] = useState({});
    const [showNestedValidation, setShowNestedValidation] = useState(false);
    const [reorderMode, setReorderMode] = useState(false);

    const {
        userInfo,
        elevatedPrivileges,
        passwordRestrictions,
        isTabsPos,
        userMenuSettings,
        setUserMenuSettings,
        menuItems,
        menuAddons,
    } = useContext(AppContext);
    const saveUserSettings = useSaveUserSettings();

    const { addonsItems } = useContext(PosContext);

    const addonItems = useMemo(() => {
        if (addonsItems) {
            return addonsItems;
        }
        return [];
    }, [addonsItems]);

    const itemLookup = useMemo(() => {
        const lookup = new Map();
        [...(menuItems || []), ...(menuAddons || []), ...(addonItems || []), item]
            .filter(Boolean)
            .forEach((candidate) => {
                const code = candidate?.item_code || candidate?.name;
                if (code) lookup.set(code, candidate);
            });
        return lookup;
    }, [menuItems, menuAddons, addonItems, item]);

    const menuBundle = useMemo(() => {
        return normalizeBundleDefinition(item?.menu_item_bundle);
    }, [item]);

    const applyBundleOrderFromSettings = useCallback((bundle) => {
        if (!bundle || !Array.isArray(bundle.categories) || bundle.categories.length === 0) {
            return bundle;
        }
        const itemCode = item?.item_code;
        if (!itemCode || !userMenuSettings) return bundle;

        const entry = userMenuSettings?.bundle_sort_indices?.[itemCode];
        if (!entry) return bundle;

        const catOrder = Array.isArray(entry?.categories) ? entry.categories : [];
        const catIndexMap = catOrder.reduce((acc, x) => {
            if (x?.name) acc[x.name] = Number(x.index);
            return acc;
        }, {});

        const originalCats = bundle.categories || [];
        const sortedCats = [...originalCats]
            .map((c, originalIndex) => ({ c, originalIndex }))
            .sort((a, b) => {
                const ia = catIndexMap[a.c?.name];
                const ib = catIndexMap[b.c?.name];
                const ra = Number.isFinite(ia) ? ia : 10_000 + a.originalIndex;
                const rb = Number.isFinite(ib) ? ib : 10_000 + b.originalIndex;
                return ra - rb;
            })
            .map(({ c }) => c);

        const optionOrderByCategory = entry?.options || {};

        const finalCats = sortedCats.map((cat) => {
            const catName = cat?.name;
            const optOrder = Array.isArray(optionOrderByCategory?.[catName])
                ? optionOrderByCategory[catName]
                : [];
            const optIndexMap = optOrder.reduce((acc, x) => {
                if (x?.item_code) acc[x.item_code] = Number(x.index);
                return acc;
            }, {});
            const opts = Array.isArray(cat?.options) ? cat.options : [];
            const sortedOpts = [...opts]
                .map((o, originalIndex) => ({ o, originalIndex }))
                .sort((a, b) => {
                    const ia = optIndexMap[a.o?.item_code];
                    const ib = optIndexMap[b.o?.item_code];
                    const ra = Number.isFinite(ia) ? ia : 10_000 + a.originalIndex;
                    const rb = Number.isFinite(ib) ? ib : 10_000 + b.originalIndex;
                    return ra - rb;
                })
                .map(({ o }) => o);
            return { ...cat, options: sortedOpts };
        });

        return { ...bundle, categories: finalCats };
    }, [item?.item_code, userMenuSettings]);

    // Local mutable bundle copy (for reorder mode). This is NOT persisted here; persistence is added in a later todo.
    const [bundleDraft, setBundleDraft] = useState(null);
    useEffect(() => {
        setBundleDraft(applyBundleOrderFromSettings(menuBundle));
    }, [menuBundle, applyBundleOrderFromSettings]);

    const persistBundleSortSetting = (nextSettings) => {
        if (!nextSettings) return;
        setUserMenuSettings?.(nextSettings);
        saveUserSettings.mutate({
            doctype: "menu_settings",
            user_settings: nextSettings,
        });
    };

    const persistCategoryOrderForItem = (categories) => {
        const itemCode = item?.item_code;
        if (!itemCode || !userMenuSettings) return;
        const next = { ...(userMenuSettings || {}) };
        next.bundle_sort_indices = { ...(next.bundle_sort_indices || {}) };
        const entry = { ...(next.bundle_sort_indices[itemCode] || {}) };
        entry.categories = (categories || [])
            .filter((c) => c?.name)
            .map((c, index) => ({ name: c.name, index }));
        entry.options = { ...(entry.options || {}) };
        next.bundle_sort_indices[itemCode] = entry;
        persistBundleSortSetting(next);
    };

    const persistOptionOrderForItem = (categoryName, options) => {
        const itemCode = item?.item_code;
        if (!itemCode || !userMenuSettings || !categoryName) return;
        const next = { ...(userMenuSettings || {}) };
        next.bundle_sort_indices = { ...(next.bundle_sort_indices || {}) };
        const entry = { ...(next.bundle_sort_indices[itemCode] || {}) };
        entry.categories = Array.isArray(entry.categories) ? entry.categories : [];
        entry.options = { ...(entry.options || {}) };
        entry.options[categoryName] = (options || [])
            .filter((o) => o?.item_code)
            .map((o, index) => ({ item_code: o.item_code, index }));
        next.bundle_sort_indices[itemCode] = entry;
        persistBundleSortSetting(next);
    };


    const hasExtraAddons = useMemo(() => {
        return getAllowedAddonCodes(item).length > 0;
    }, [item]);
    const isNotesOnly = useMemo(() => {
        return !(menuBundle && menuBundle.categories) && !hasExtraAddons;
    }, [menuBundle, hasExtraAddons]);

    const [qty, setQty] = useState(1);
    const [notes, setNotes] = useState("");
    const [isEditingPrice, setIsEditingPrice] = useState(false);

    const [itemRate, setItemRate] = useState(0);
    const [total, setTotal] = useState(0);
    const [addonsTotal, setAddonsTotal] = useState(0);

    const reset = () => {
        setSelectedAddons([]);
        setSelectedOptions({});
        setQty(1);
        setNotes("");
        setItemRate(0);
        setTotal(0);
        setAddonsTotal(0);
        setIsEditingPrice(false);
        setBundleErrors({});
        setShowNestedValidation(false);
    };



    useEffect(() => {
        if (editedOrderItem) {
            setItemRate(getItemBaseRate(editedOrderItem.item));
            const tmpQty = editedOrderItem.qty;
            setNotes(editedOrderItem.notes);
            setQty(tmpQty);
            const existingAddons = Array.isArray(editedOrderItem.addons)
                ? editedOrderItem.addons
                : [];
            const initialized = menuBundle
                ? initializeBundleSelections(
                      menuBundle,
                      existingAddons,
                      itemLookup,
                      {
                          parentAbsoluteQty: tmpQty,
                          rootItemCode: item?.item_code,
                      }
                  )
                : { selectedOptions: {}, usedIndices: new Set() };

            setSelectedOptions(initialized.selectedOptions);
            const allowedAddons = getAllowedAddonCodes(item);
            setSelectedAddons(
                existingAddons
                    .map((addon, index) => ({ addon, index }))
                    .filter(({ addon, index }) => {
                        const code = getAddonCode(addon);
                        return (
                            !initialized.usedIndices.has(index) &&
                            allowedAddons.includes(code)
                        );
                    })
                    .map(({ addon }) => getAddonCode(addon))
            );
        } else {
            if (item) {
                const { notes } = item;
                const baseRate = getItemBaseRate(item);
                setItemRate(baseRate);
                setNotes(notes);
                setQty(1);
                setTotal(baseRate * 1);

                setSelectedOptions(
                    menuBundle
                        ? initializeBundleSelections(menuBundle, [], itemLookup, {
                              rootItemCode: item?.item_code,
                          }).selectedOptions
                        : {}
                );
                setSelectedAddons([]);
            } else {
                setItemRate(0);
                setQty(1);
                setNotes("");
                setSelectedOptions({});
            }
        }
        setBundleStep(0);
        setBundleErrors({});
        setShowNestedValidation(false);
        setReorderMode(false);
    }, [item, open, editedOrderItem, menuBundle, itemLookup]);

    useEffect(() => {
        const calculateAddonsTotal = () => {
            const base = itemRate * qty;
            const unitAddons = pruneSkippedNestedSelections(
                item,
                composeUnitAddonSelection(
                    selectedOptions,
                    selectedAddons,
                    itemLookup
                ),
                itemLookup
            );
            const computedAddonsTotal = calculateAddonTreeTotal(
                unitAddons,
                qty,
                itemLookup
            );

            setTotal(base + computedAddonsTotal);
            setAddonsTotal(computedAddonsTotal);
        };

        const timer = setTimeout(calculateAddonsTotal, 100);

        return () => {
            clearTimeout(timer);
        };
    }, [qty, selectedAddons, selectedOptions, itemRate, itemLookup, item]);

    const handleNotesChange = (event) => {
        setNotes(event.target.value);
    };

    const addQty = (addedQty) => {
        if (qty + addedQty > 0) setQty(qty + addedQty);
    };

    const handleClose = () => {
        reset();
        if (onClose) {
            onClose();
        }
    };

    const handleClick = () => {
        const unitAddons = pruneSkippedNestedSelections(
            item,
            composeUnitAddonSelection(
                selectedOptions,
                selectedAddons,
                itemLookup
            ),
            itemLookup
        );
        const validationErrors = validateItemSelection(item, unitAddons, itemLookup);
        if (validationErrors.length > 0) {
            const rootErrors = {};
            validationErrors
                .filter((error) => error.itemCode === item?.item_code)
                .forEach((error) => {
                    if (error.category) rootErrors[error.category] = error.message || t("bundle.required");
                });
            setBundleErrors(rootErrors);
            setShowNestedValidation(true);
            const firstError = validationErrors[0];
            const firstMissingIdx = (menuBundle?.categories || []).findIndex(
                (category) => category?.name === firstError.topCategory
            );
            if (firstMissingIdx >= 0) setBundleStep(firstMissingIdx);
            enqueueSnackbar(
                t("bundle.completeCustomizationFor", {
                    category:
                        firstError.path?.join(" / ") ||
                        firstError.category ||
                        t("bundle.requiredCategory"),
                }),
                { variant: "warning" }
            );
            return;
        }
        setBundleErrors({});
        setShowNestedValidation(false);

        const orderAddons = materializeAddonTree(unitAddons, qty, itemLookup);

        const hasValidItemRate =
            itemRate !== "" &&
            Number.isFinite(Number(itemRate)) &&
            Number(itemRate) >= 0;
        const normalizedItemRate = hasValidItemRate
            ? Number(itemRate)
            : getItemBaseRate(editedOrderItem?.item || item);

        if (editedOrderItem) {
            const updatedEntry = {
                ...editedOrderItem,
                qty,
                addons: orderAddons,
                notes,
            };

            if (updatedEntry.item) {
                updatedEntry.item.qty = qty;
                updatedEntry.item.addons = orderAddons;
                updatedEntry.item.notes = notes;
                // Update the rate if admin edited it
                if (isRoleAdmin(userInfo.role_profile_name)) {
                    updatedEntry.item.standard_rate = normalizedItemRate;
                    updatedEntry.item.rate = normalizedItemRate;
                }
            }
            onUpdateItem(updatedEntry, false);
        } else {
            // Create item with potentially edited rate for admin users
            const rateToUse = isRoleAdmin(userInfo.role_profile_name)
                ? normalizedItemRate
                : getItemBaseRate(item);
            const itemToAdd = {
                ...item,
                qty: qty,
                notes: notes,
                // Keep temporary cart display in sync with edited prices.
                standard_rate: rateToUse,
                rate: rateToUse,
            };
            onAddItem(itemToAdd, qty, orderAddons, notes, true);
        }
        handleClose();
        // setTimeout(reset, 1000);
    };

    const handleCheckboxChange = (event) => {
        const { checked, value } = event.target;

        setSelectedAddons((prev) => {
            if (checked) {
                return _.xor(prev, [value]);
            } else {
                return _.without(selectedAddons, value);
            }
        });
    };

    const handlePriceChange = (event) => {
        const value = event.target.value;
        // Allow empty string during editing
        if (value === "") {
            setItemRate("");
        } else {
            const newPrice = parseFloat(value);
            if (!isNaN(newPrice) && newPrice >= 0) {
                setItemRate(newPrice);
            }
        }
    };

    const handlePriceFocus = (event) => {
        // Select all text when field is focused
        event.target.select();
    };

    const handlePriceEditStart = () => {
        setIsEditingPrice(true);
    };

    const handlePriceEditSave = () => {
        // If field is empty or invalid, revert to original value
        if (itemRate === "" || itemRate < 0 || isNaN(itemRate)) {
            const originalRate = editedOrderItem
                ? getItemBaseRate(editedOrderItem.item)
                : getItemBaseRate(item);
            setItemRate(originalRate);
        }
        setIsEditingPrice(false);
    };

    const handlePriceEditCancel = () => {
        // Revert to original value
        const originalRate = editedOrderItem
            ? getItemBaseRate(editedOrderItem.item)
            : getItemBaseRate(item);
        setItemRate(originalRate);
        setIsEditingPrice(false);
    };

    const handlePriceKeyPress = (event) => {
        if (event.key === "Enter") {
            handlePriceEditSave();
        } else if (event.key === "Escape") {
            handlePriceEditCancel();
        }
    };

    return item ? (
        <>
            <ItemDialog
                className={classes.DialogWrap}
                onClose={() => {
                    handleClose();
                }}
                open={open}
                PaperProps={
                    isTabsPos
                        ? {
                              style: {
                                  width: "90vw",
                                  maxWidth: "unset",
                              },
                          }
                        : undefined
                }
            >
                <DialogContent className={classes.ContentWrapper}>
                    <div>
                        <button className="BackBtn">
                            <img
                                src={backIcon}
                                onClick={() => {
                                    handleClose();
                                }}
                                alt={t("common.back")}
                            />
                        </button>
                    </div>

                    <div className={classes.Header}>
                        <div className={classes.ItemDesc}>
                            <img
                                className={classes.ItemDescImage}
                                src={item.cover}
                                alt={item.item_name}
                            />
                            <div>
                                <div className={classes.ItemDescName}>
                                    {<MenuText>{item.item_name}</MenuText>}
                                </div>
                                {isRoleAdmin(userInfo.role_profile_name) ? (
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                        }}
                                    >
                                        {isEditingPrice ? (
                                            <TextField
                                                type="number"
                                                value={itemRate}
                                                onChange={handlePriceChange}
                                                onFocus={handlePriceFocus}
                                                onKeyDown={handlePriceKeyPress}
                                                onBlur={handlePriceEditSave}
                                                variant="outlined"
                                                size="small"
                                                autoFocus
                                                inputProps={{
                                                    min: 0,
                                                    step: 0.01,
                                                    style: { fontSize: "14px" },
                                                }}
                                                sx={{
                                                    "& .MuiOutlinedInput-root":
                                                        {
                                                            height: "32px",
                                                            "& fieldset": {
                                                                borderColor:
                                                                    "#ddd",
                                                            },
                                                            "&:hover fieldset":
                                                                {
                                                                    borderColor:
                                                                        "#999",
                                                                },
                                                            "&.Mui-focused fieldset":
                                                                {
                                                                    borderColor:
                                                                        "#1976d2",
                                                                },
                                                        },
                                                }}
                                            />
                                        ) : (
                                            <>
                                                <div
                                                    className={
                                                        classes.ItemDescPrice
                                                    }
                                                >
                                                    {itemRate || 0}
                                                </div>
                                                {elevatedPrivileges || passwordRestrictions.price ||
                                                isRoleAdmin(
                                                    userInfo.role_profile_name
                                                ) ? (
                                                    <IconButton
                                                        size="small"
                                                        onClick={
                                                            handlePriceEditStart
                                                        }
                                                        sx={{
                                                            padding: "4px",
                                                            "&:hover": {
                                                                backgroundColor:
                                                                    "rgba(0, 0, 0, 0.04)",
                                                            },
                                                        }}
                                                    >
                                                        ✏️
                                                    </IconButton>
                                                ) : (
                                                    <></>
                                                )}
                                            </>
                                        )}
                                    </div>
                                ) : (
                                    <div className={classes.ItemDescPrice}>
                                        {getItemBaseRate(item)}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className={classes.ItemQty}>
                            <ItemSmallButton
                                onClick={() => addQty(-1)}
                                className={classes.ItemQtyMinus}
                            >
                                -
                            </ItemSmallButton>
                            <NumPad.Number
                                onChange={(value) => {
                                    setQty(value > 0 ? toInteger(value) : 1);
                                }}
                                isDecimal={false}
                                position={"startBottomRight"}
                                min={1}
                                value={0}
                            >
                                <div className={classes.ItemQtyText}>{qty}</div>
                            </NumPad.Number>
                            <ItemSmallButton
                                onClick={() => addQty(1)}
                                className={classes.ItemQtyPlus}
                            >
                                +
                            </ItemSmallButton>
                        </div>
                    </div>

                    <div
                        className={classes.ScrollableContent}
                        style={{
                            overflow: menuBundle && menuBundle.categories ? "hidden" : "auto",
                        }}
                    >
                        {menuBundle && menuBundle.categories ? (
                            <BundleConfigurator
                                item={item}
                                menuBundle={bundleDraft || menuBundle}
                                addonItems={addonItems}
                                menuAddons={menuAddons}
                                isTabsPos={!!isTabsPos}
                                selectedOptions={selectedOptions}
                                setSelectedOptions={setSelectedOptions}
                                selectedAddons={selectedAddons}
                                setSelectedAddons={setSelectedAddons}
                                bundleStep={bundleStep}
                                setBundleStep={setBundleStep}
                                bundleErrors={bundleErrors}
                                setBundleErrors={setBundleErrors}
                                showValidation={showNestedValidation}
                                itemLookup={itemLookup}
                                classes={classes}
                                qty={qty}
                                reorderMode={reorderMode}
                                onToggleReorder={(next) => setReorderMode(!!next)}
                                onReorderCategories={({ oldIndex, newIndex }) => {
                                    let nextCategories = null;
                                    setBundleDraft((prev) => {
                                        if (!prev || !Array.isArray(prev.categories)) return prev;
                                        nextCategories = arrayMove(prev.categories, oldIndex, newIndex);
                                        return {
                                            ...prev,
                                            categories: nextCategories,
                                        };
                                    });
                                    if (nextCategories) {
                                        persistCategoryOrderForItem(nextCategories);
                                    }
                                    setBundleStep((prevStep) => {
                                        // Keep the same category selected after reorder when possible.
                                        if (prevStep === oldIndex) return newIndex;
                                        if (oldIndex < prevStep && newIndex >= prevStep) return prevStep - 1;
                                        if (oldIndex > prevStep && newIndex <= prevStep) return prevStep + 1;
                                        return prevStep;
                                    });
                                }}
                                onReorderOptions={({ categoryName, oldIndex, newIndex }) => {
                                    if (!categoryName) return;
                                    let nextOptions = null;
                                    setBundleDraft((prev) => {
                                        if (!prev || !Array.isArray(prev.categories)) return prev;
                                        const cats = prev.categories.map((c) => {
                                            if (c?.name !== categoryName) return c;
                                            const opts = Array.isArray(c?.options) ? c.options : [];
                                            nextOptions = arrayMove(opts, oldIndex, newIndex);
                                            return { ...c, options: nextOptions };
                                        });
                                        return { ...prev, categories: cats };
                                    });
                                    if (nextOptions) {
                                        persistOptionOrderForItem(categoryName, nextOptions);
                                    }
                                }}
                                showNotes={!kioskMode}
                                notes={notes}
                                setNotes={setNotes}
                            />
                        ) : (
                            <>
                                 {hasExtraAddons ? (
                                     <div className={classes.Addons}>
                                        <div>{t("bundle.extraAddons")}</div>
                                         <FormGroup
                                             className={classes.AddonsList}
                                         >
                                             {hasExtraAddons
                                                 ? getAllowedAddonCodes(item).map(
                                                       (addonsItem) => {
                                                           const addon =
                                                               addonItems.find(
                                                                   (i) =>
                                                                       i.item_code ===
                                                                       addonsItem
                                                               ) || {};
                                                           return (
                                                               <div
                                                                   className={
                                                                       classes.AddonsRaw
                                                                   }
                                                                   key={addonsItem}
                                                               >
                                                                   <FormControlLabel
                                                                       control={
                                                                           <Checkbox
                                                                               onChange={
                                                                                   handleCheckboxChange
                                                                               }
                                                                               checked={
                                                                                   selectedAddons.find(
                                                                                       (i) =>
                                                                                           i ===
                                                                                           addonsItem
                                                                                   ) !=
                                                                                   null
                                                                               }
                                                                               value={
                                                                                   addonsItem
                                                                               }
                                                                           />
                                                                       }
                                                                       label={`${addonsItem} `}
                                                                   />
                                                                   <span>
                                                                       {addon.standard_rate}
                                                                   </span>
                                                               </div>
                                                           );
                                                       }
                                                   )
                                                 : ""}
                                         </FormGroup>
                                     </div>
                                 ) : null}
                            </>
                        )}

                        {/* Notes: standalone only when there is no bundle */}
                        {!kioskMode && !(menuBundle && menuBundle.categories) ? (
                            <div className={classes.Notes}>
                                <div>{t("bundle.notes")}</div>
                                <textarea
                                    value={notes}
                                    onChange={handleNotesChange}
                                    placeholder={t("bundle.addNotesHere")}
                                    className={
                                        isNotesOnly
                                            ? `${classes.NotesArea} ${classes.NotesAreaLarge}`
                                            : classes.NotesArea
                                    }
                                ></textarea>
                            </div>
                        ) : null}
                    </div>

                    <div className={classes.Footer}>
                        <div>
                            <div className={classes.TotalText}>
                                {t("pos.total")}
                            </div>
                            <div>
                                <div className={classes.TotalPrice}>
                                    {total}
                                </div>
                                <div className={classes.AddonsPrice}>
                                    ({t("bundle.addonsLabel")} {addonsTotal})
                                </div>
                            </div>
                        </div>
                        {isTabsPos ? (
                            <div className={classes.FooterActions}>
                                <ButtonBase
                                    onClick={handleClose}
                                    className={classes.CancelAction}
                                    disabled={pushItemWorker.isLoading}
                                >
                                    {t("common.cancel")}
                                </ButtonBase>
                                <ButtonBase
                                    onClick={handleClick}
                                    className={classes.DialogAction}
                                    disabled={pushItemWorker.isLoading}
                                >
                                    {pushItemWorker.isLoading
                                        ? t("common.saving")
                                        : kioskMode ? t(editedOrderItem ? "kiosk.updateItem" : "kiosk.addItem") : t("common.save")}
                                </ButtonBase>
                            </div>
                        ) : (
                            <ButtonBase
                                onClick={handleClick}
                                className={classes.DialogAction}
                            >
                                {pushItemWorker.isLoading
                                    ? t("common.saving")
                                    : kioskMode ? t(editedOrderItem ? "kiosk.updateItem" : "kiosk.addItem") : t("common.save")}
                            </ButtonBase>
                        )}
                    </div>
                </DialogContent>
            </ItemDialog>
        </>
    ) : (
        ""
    );
}

export default AddItemWidget;
